import { useContext, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { createSearchParams, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { StyleContext } from "../providers/StyleContextProvider";
import { RecruiterMenu } from "../components/Shared/RecruiterMenu";
import { ReactComponent as SearchIcon } from '../assets/svg/search.svg';
import { Button } from "../components/Shared/Buttons/Button";
import { ReactComponent as LeftArrow } from "../assets/svg/left_arrow.svg";
import { useMediaQuery } from "react-responsive";
import { SingleSelect } from "../components/Shared/SingleSelect";
import { SelectInputs } from "../models/SelectInputs";
import { CheckboxInput } from "../components/Shared/Inputs/CheckboxInput";
import { VacancyCountLoader } from "../components/Loaders/VacancyCountLoader";
import { VacancyListLoader } from "../components/Loaders/VacancyListLoader";
import { ReactComponent as Calendar } from "../assets/svg/calendar.svg";
import useErrorPage from "../hooks/useErrorPage";
import { LoggedRecruiter } from "../models/LoggedRecruiter";
import { AuthContext } from "../providers/AuthContextProvider";
import { ApplicantsSearchCriteria } from "../models/ApplicantsSearchCriteria";
import { 
    downloadApplicantResumes, 
    updateApplicationStatuses, 
    getApplicationStatuses, 
    getApplicants, 
    updateApplicationReadStatus, 
    getCandidateCvDetails
} from "../services/RecruiterService";
import { FeedbackCard } from "../components/Shared/FeedbackCard";
import { ApplicationStatusModel } from "../models/ApplicationStatusModel";
import { Pagination } from "../components/Shared/Pagination";
import { Footer } from "../components/Shared/Footer";
import { formatDateWithSuperscript, getCandidateCVUrlByRecruiter } from "../helpers/Common";
import { PopUpWindow } from "../components/Shared/PopUpWindow";
import { ReactComponent as Crown } from '../assets/svg/crown.svg';
import { ReactComponent as Arrow } from '../assets/svg/drop_down.svg';
import { ReactComponent as TickMark } from '../assets/svg/tick_mark.svg';
import { ReactComponent as ThreeDots } from '../assets/svg/three_dots.svg';
import { ReactComponent as CVLess } from '../assets/svg/cv_less.svg';
import { ReactComponent as DefaultUser } from '../assets/svg/default_user_black.svg';
import { ReactComponent as Document } from '../assets/svg/document.svg';
import { ReactComponent as Answers } from '../assets/svg/answers.svg';
import { ReactComponent as Move } from '../assets/svg/move.svg';
import { ReactComponent as Notes } from '../assets/svg/notes.svg';
import { ProfileEntrance } from "../components/Home/ProfileEntrance";
import { JobApplication } from "../models/JobApplication";
import { TabItem } from "../models/TabItem";
import { StatusTab } from "../components/ApplicationStatus/StatusTab";
import { CVTab } from "../components/ApplicationStatus/CVTab";
import { AnswersTab } from "../components/ApplicationStatus/AnswersTab";
import { NotesTab } from "../components/ApplicationStatus/NotesTab";
import { InformationTab } from "../components/ApplicationStatus/InformationTab";
import { QuizTab } from "../components/ApplicationStatus/QuizTab";
import { CoverLetterTab } from "../components/ApplicationStatus/CoverLetterTab";
import { ApplicationTabPanel } from "../components/ApplicationStatus/ApplicationTabPanel";
import { LazyProfileImage } from "../components/Shared/LazyProfileImage";
import { CandidateTabPanelLoader } from "../components/Loaders/CandidateTabPanelLoader";
import { EmailToCandidate } from "../components/Shared/EmailToCandidate";
import { useAlert } from "../hooks/useAlert";
import { UserType } from "../enums/UserType";
import { ApplicationStatusPageTexts } from "../helpers/LayoutTexts";

export const ApplicationStatusPage: React.FC = () => {   
    const navigate = useNavigate();
    let params = useParams();
    const location = useLocation();
    const jobId: number = +params.jobId!;
    const [searchParams] = useSearchParams();
    const jobTitle: string | null = searchParams.get("title");
    const page: number = searchParams.get("page") === null ? 1 : +searchParams.get("page")!;
    const pageSize: number = searchParams.get("pageSize") === null ? 20 : +searchParams.get("pageSize")!;
    const statusId: number = searchParams.get("statusId") === null ? 0 : +searchParams.get("statusId")!;
    const styleContext = useContext(StyleContext);
    const styles = styleContext.getComponentStyle("applicationStatusPage");
    const authContext = useContext(AuthContext);
    const recruiter: LoggedRecruiter | null = authContext.getLoggedRecruiter();
    const [keyword, setKeyword] = useState<string>("");   
    const [appReadStatus, setAppReadStatus] = useState<number>(2);   
    const [actionStatus, setActionStatus] = useState<string>("All");   
    const [applicantsLoading, setApplicantsLoading] = useState<boolean>(true);
    const [statusLoading, setStatusLoading] = useState<boolean>(true);
    const [openMessageDialog, setOpenMessageDialog] = useState<boolean>(false);
    const [applicants, setApplicants] = useState<JobApplication[]>([]);
    const [applicationStatuses, setApplicationStatuses] = useState<any[]>([]);
    const [totalApplications, setTotalApplications] = useState<number>(0);
    const [totalPages, setTotalPages] = useState<number>();
    const [hoveredIndex, setHoveredIndex] = useState<number>(-1);
    const [allSelect, setAllSelect] = useState<boolean>(false);  
    const [singleSelect, setSingleSelect] = useState<Record<number, boolean>>({});
    const [isCvLess, setIsCvLess] = useState<boolean>();  
    const [openSideMenu, setOpenSideMenu] = useState<boolean>(false); 
    const [openQuotaDialog, setOpenQuotaDialog] = useState<boolean>(false);
    const [hoveredPremiumApplicant, setHoveredPremiumApplicant] = useState<number | null>(null);
    const [tabItems, setTabItems] = useState<TabItem[]>([]);
    const [selectedApplicant, setSelectedApplicant] = useState<JobApplication>();    
    const [displayTabs, setDisplayTabs] = useState<boolean>(false);
    const [profileLoading, setProfileLoading] = useState<boolean>(true);
    const [activeTab, setActiveTab] = useState<string>("CV");
    const candidateSortRef = useRef<HTMLDivElement>(null);
    const vacancyListParamsRef = useRef<string | undefined>(location.state?.vacancyListParams);
    const [openStatusDropdownId, setOpenStatusDropdownId] = useState<number | null>(null);
    const statusDropdownRef = useRef<HTMLDivElement>(null);
    const [openActionsMenuId, setOpenActionsMenuId] = useState<number | null>(null);
    const [unreadCount, setUnreadCount] = useState<number>(0);
    const actionsMenuRef = useRef<HTMLDivElement>(null);
    const actionsSelectRef = useRef<HTMLDivElement>(null);
    const hasSelection = allSelect || Object.values(singleSelect).some(Boolean);
    const [recipientEmails, setRecipientEmails] = useState<string[]>([]);
    const [candidateIds, setCandidateIds] = useState<string>("");
    const [showEmailComponent, setShowEmailComponent] = useState<boolean>(false);
    const [openActionsSelectDropdown, setOpenActionsSelectDropdown] = useState<boolean>(false);
    const [hoveredStatusOption, setHoveredStatusOption] = useState<number | null>(null);
    const STAGES = ["All", "New", "Screening", "Shortlisted", "Quiz", "Interview", "Decision", "Unresponsive"];
    const STAGE_STATUS_MAP = ApplicationStatusPageTexts.STAGE_STATUS_MAP;
    const [selectedStage, setSelectedStage] = useState<string>("All");
    const [nameSortAsc, setNameSortAsc] = useState<boolean>(false);
    const sortOrder = "CreatedDate DESC";
    const isMobile = useMediaQuery({ query: "(max-width: 786px)" });
    const MAX_RECIPIENTS = 25;
    const [popupMounted, setPopupMounted] = useState<boolean>(false);
    const [popupVisible, setPopupVisible] = useState<boolean>(false);
    const POPUP_ANIMATION_MS = 350;
    const { Alert, openAlert } = useAlert();
    const [toErrorPage] = useErrorPage();

    const APP_READ_STATUS_LABELS: Record<number, string> = {
        2: "All Applications",
        1: "Opened Applications",
        0: "Unread Applications",
        4: "Name A-Z"
    };

    const sortBySelectInputs: SelectInputs = {
        default: nameSortAsc ? "Name A-Z" : (APP_READ_STATUS_LABELS[appReadStatus] ?? "All Applications"),
        options: [
            {"value": 2, "option": "All Applications"},
            {"value": 1, "option": "Opened Applications"},
            {"value": 0, "option": "Unread Applications"},
            ...(isCvLess ? [{"value": 3, "option": "Applicants With CVs"}] : []),
            {"value": 4, "option": "Name A-Z"}
        ]
    }

    const perPageSelectInputs: SelectInputs = {
        default: pageSize.toString(),
        options: [            
            {"value": 15, "option": "15"},
            {"value": 20, "option": "20"},
            {"value": 30, "option": "30"},
            {"value": 60, "option": "60"}
        ]
    }
    
    useEffect(() => {
        if (!isMobile) {
            if (displayTabs) {
                document.body.style.overflow = 'hidden';
            } else {
                document.body.style.overflow = '';
            }
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [displayTabs]);

    useEffect(() => {
        if (!recruiter) {
            const applicationUrl = `recruiter/vacancy/applications/${jobId}`;
            const params = { callbackUrl: applicationUrl, message: "Please login to view received applications." };
            navigate({
                pathname: '/recruiter/account/login',
                search: `?${createSearchParams(params)}`
            });
        }
        else {
            getApplicationStatusList();
        }
    }, []);

    useEffect(() => {
        getApplicantList();
        setAllSelect(false);
        setSingleSelect({});
        window.scrollTo({top: 0, behavior: 'smooth'});
    }, [page, pageSize, appReadStatus, statusId]);

    useEffect(() => {
        const handleClickOutsideStatusDropdown = (event: MouseEvent) => {
            if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
                setOpenStatusDropdownId(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutsideStatusDropdown);
        return () => {
            document.removeEventListener('mousedown', handleClickOutsideStatusDropdown);
        };
    }, []);

    useEffect(() => {
        const handleClickOutsideActionsMenu = (event: MouseEvent) => {
            if (actionsMenuRef.current && !actionsMenuRef.current.contains(event.target as Node)) {
                setOpenActionsMenuId(null);
            }
            if (actionsSelectRef.current && !actionsSelectRef.current.contains(event.target as Node)) {
                setOpenActionsSelectDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutsideActionsMenu);
        return () => {
            document.removeEventListener('mousedown', handleClickOutsideActionsMenu);
        };
    }, []);

    useEffect(() => {
        const shouldShow = !isMobile && displayTabs;
        let timeoutId: ReturnType<typeof setTimeout>;
        if (shouldShow) {
            setPopupMounted(true);
        } else if (popupMounted) {
            setPopupVisible(false);
            timeoutId = setTimeout(() => setPopupMounted(false), POPUP_ANIMATION_MS);
        }
        return () => clearTimeout(timeoutId);
    }, [isMobile, displayTabs]);

    useEffect(() => {
        if (popupMounted && !isMobile && displayTabs) {
            setPopupVisible(true);
        }
    }, [popupMounted]);

    const setPageSize = (selectedPageSize: number) => {
        searchParams.set("page", "1");
        searchParams.set("pageSize", selectedPageSize.toString());
        const newUrl = `?${searchParams}`;
        navigate(newUrl);
    }

    const handleActionStatusSelect = (status: any) => {
        setOpenActionsSelectDropdown(false);
        setHoveredStatusOption(null);
        setActionStatus(status.status);
        setOpenMessageDialog(true);
    }

    const handleAllSelectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = event.target.checked;
        const newSelection: Record<number, boolean> = {};
        filteredApplicants.forEach(applicant => {
            if (applicant.deleted || !applicant.hasSentToRecruiter) {
                return;
            }
            const id = (applicant.hasSentToRecruiter && applicant.preApplicationId) ? applicant.preApplicationId : applicant.jobApplicationId;
            newSelection[id] = isChecked;
        });
        setSingleSelect(newSelection);
        setAllSelect(isChecked);
    };

    const handleSingleSelectChange = (id: number) => {
        const selectableApplicants = filteredApplicants.filter(applicant => 
            !applicant.deleted && applicant.hasSentToRecruiter
        );
        setSingleSelect(prev => {
            const newSelection = {
                ...prev,
                [id]: !prev[id]
            };
            var allSelected = selectableApplicants.length ===  Object.values(newSelection).filter(Boolean).length;
            setAllSelect(allSelected);
            return newSelection;
        });
    };

    const handlePagination = (selectedPage: number) => {
        searchParams.set("page", selectedPage.toString());
        const newUrl = `?${searchParams}`;
        navigate(newUrl);       
    } 

    const handleSearch = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        getApplicantList(true);
    }

    const closeMessageDialog = () => {
        setOpenMessageDialog(false);
        setHoveredStatusOption(null);
    }

    const getStageStatuses = (stage: string) => {
        return applicationStatuses.filter(status => STAGE_STATUS_MAP[stage]?.includes(status.status));
    };

    const getStageTotal = (stage: string) => {
        if (stage === "All") {
            return totalApplications;
        }
        return getStageStatuses(stage).reduce((sum, status) => sum + (status.applicationsCount || 0), 0);
    };

    const getGroupedStatusOptions = (statuses: any[]) => {
        return Object.keys(STAGE_STATUS_MAP)
            .map((stage) => ({
                stage,
                items: statuses.filter((status) => STAGE_STATUS_MAP[stage]?.includes(status.status))
            }))
            .filter((group) => group.items.length > 0);
    };

    const stageStatuses = getStageStatuses(selectedStage);
    const groupedStatusOptions = getGroupedStatusOptions(applicationStatuses);

    const handleSortBySelect = (selectedValue: number) => {
        if (selectedValue === 4) {
            setNameSortAsc(true);
        } else {
            setNameSortAsc(false);
            setAppReadStatus(selectedValue);
        }
    };

    const filteredApplicants = (
        selectedStage === "All"
            ? applicants
            : applicants.filter(applicant =>
                STAGE_STATUS_MAP[selectedStage]?.includes(applicant.applicationStatus?.status ?? "")
            )
    ).slice().sort((a, b) => {
        if (!nameSortAsc) return 0;
        const nameA = `${a.candidate?.forename ?? ""} ${a.candidate?.surname ?? ""}`.trim().toLowerCase();
        const nameB = `${b.candidate?.forename ?? ""} ${b.candidate?.surname ?? ""}`.trim().toLowerCase();
        return nameA.localeCompare(nameB);
    });
    
    const handleStageChange = (stage: string) => {
        setSelectedStage(stage);
        setSingleSelect({});
        setAllSelect(false);
        searchParams.delete("page");
        searchParams.delete("pageSize");
        if (statusId !== 0) {
            searchParams.set("statusId", "0");
        }
        navigate(`?${searchParams}`);
    };

    const visibleStages = STAGES.filter(stage => 
        stage === "All" || getStageStatuses(stage).length > 0
    );

    const currentViewTotal = selectedStage === "All"
        ? (applicants[0]?.totalRecordsCount ?? 0)
        : getStageTotal(selectedStage);

    function lightenColor(hex: string, alpha: number): string {
        // Remove any leading/trailing whitespace from the hex string
        hex = hex.trim();
        // Converts a hex color string to an rgba color string with given transparency
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    const handleSideMenuToggle = () => {        
        setOpenSideMenu((prev) => !prev);
    }

    const handleApplicantClick = (e: React.MouseEvent<HTMLElement>, applicant: JobApplication, tab?: string) => {
        e.stopPropagation();
        if (!applicant.hasSentToRecruiter) {
            setOpenQuotaDialog(true);
        }
        else if (!applicant.deleted) {
            setProfileLoading(true);
            setDisplayTabs(true);
            openPopUpComponent(applicant);
            if (tab) {
                setActiveTab(tab);
            } else {
                setActiveTab(applicant.jobApplicationId > 0 ? "CV" : "Answers");
            }
        }           
    };

    const closeQuotaDialog = () => {
        setOpenQuotaDialog(false);
    }

    const handleStatusChange = (statusId: number) => {
        searchParams.set("page", "1");
        searchParams.set("statusId", statusId.toString());
        navigate(`?${searchParams}`);
    }

    const toggleStatusDropdown = (e: React.MouseEvent, id: number) => {
        e.stopPropagation();
        setOpenStatusDropdownId(prev => prev === id ? null : id);
    }

    const toggleActionsMenu = (e: React.MouseEvent, id: number) => {
        e.stopPropagation();
        setOpenActionsMenuId(prev => prev === id ? null : id);
    }

    const handleActionMenuItemClick = (e: React.MouseEvent<HTMLElement>, applicant: JobApplication, tab: string) => {
        setOpenActionsMenuId(null);
        handleApplicantClick(e, applicant, tab);
    }

    const handleStatusOptionClick = (e: React.MouseEvent, applicant: JobApplication, status: any, id: number) => {
        e.stopPropagation();
        setOpenStatusDropdownId(null);
        setHoveredStatusOption(null);
        if (status.status === applicant.applicationStatus?.status) {
            return;
        }
        setSingleSelect({ [id]: true });
        setAllSelect(false);
        setActionStatus(status.status);
        setOpenMessageDialog(true);
    }

    const handlePanelStatusOptionClick = (status: any) => {
        if (!selectedApplicant || status.status === selectedApplicant.applicationStatus?.status) {
            return;
        }
        const id = (selectedApplicant.hasSentToRecruiter && selectedApplicant.preApplicationId) ? selectedApplicant.preApplicationId : selectedApplicant.jobApplicationId;
        setSingleSelect({ [id]: true });
        setAllSelect(false);
        setActionStatus(status.status);
        setOpenMessageDialog(true);
    }

    const openPopUpComponent = (applicant : JobApplication) => {
        if (!applicant?.hasReadByRecruiter) {
            updateReadStatus(applicant);
        }
        setSelectedApplicant(applicant);
        let tabsList = [
            (applicant.jobApplicationId !== 0) && {label: "CV", value: "CV", component: <CVTab recruiterId={recruiter!.recruiterId} candidateId={applicant.candidate?.candidateId!} token={recruiter!.token} />},
            ((applicant.hasAnswered || applicant.preApplicationId !== 0) && applicant.applicationStatus?.status !== "Screening Incomplete") && { label: "Answers", value: "Answers", component: <AnswersTab selectedApplicant={applicant} jobId={jobId} isCvLess={isCvLess!} />},
            (applicant.hasQuizPassed) && { label: "Quiz", value: "Quiz", component: <QuizTab jobApplicationId={applicant.jobApplicationId} />},
            { label: "Status", value: "Status", component: <StatusTab selectedApplicant={applicant} isCvLess={isCvLess!} />},
            { label: "Notes", value: "Notes", component: <NotesTab jobApplicationId={applicant.jobApplicationId} preApplicationId={applicant.preApplicationId} isCvLess={isCvLess!} />},
            !(isMobile && (applicant.jobApplicationId !== 0)) && { label: "Information", value: "Information", component: <InformationTab candidateId={applicant.candidate?.candidateId!} recruiterId={recruiter!.recruiterId} token={recruiter!.token} />},
            (applicant.hasCoverLetter) && { label: "Cover Letter", value: "Cover Letter", component: <CoverLetterTab jobApplicationId={applicant.jobApplicationId} />}
        ];
        const newTabItems = tabsList.filter(Boolean) as TabItem[];
        setTabItems(newTabItems);
        setProfileLoading(false);
    }

    const closePopUp = () => {
        setDisplayTabs(false);
    }

    const openEmailComponent = () => {
        const selectedCount = Object.values(singleSelect).filter(Boolean).length;
        if (selectedCount > MAX_RECIPIENTS) {
            openAlert("error", `You can select a maximum of ${MAX_RECIPIENTS} recipients at once.`);
            return;
        }
        let selectedIds: number[] = [];
        let selectedEmails: string[] = [];
        applicants.forEach(applicant => {
            const id = (applicant.preApplicationId && applicant.preApplicationId > 0)
                ? applicant.preApplicationId
                : applicant.jobApplicationId;
            if (singleSelect[id]) {
                const candidateId = applicant.candidate?.candidateId;
                if (candidateId) {
                    selectedIds.push(candidateId);
                }
                if (applicant.candidate?.email && applicant.candidate.email.trim() !== '') {
                    selectedEmails.push(applicant.candidate.email);
                }
            }
        });
        setRecipientEmails(selectedEmails);
        setCandidateIds(selectedIds.join(','));
        setShowEmailComponent(true);
    }

    const closeEmailComponent = () => {
        setShowEmailComponent(false);
    }

    const navigateToHome = () => {
        const lastParams = vacancyListParamsRef.current;
        navigate(`/recruiter/vacancy${lastParams  ? `?${lastParams }` : ''}`);
    }

    const handleUnreadClick = () => {
        handleSortBySelect(0);
        setSelectedStage("All");
        if (statusId !== 0) {
            searchParams.set("statusId", "0");
            navigate(`?${searchParams}`);
        }
    };

    async function getApplicationStatusList() {
        setStatusLoading(true);
        await getApplicationStatuses(jobId, recruiter!.recruiterId, recruiter!.isOrganizationAdmin, recruiter!.token)
        .then((statusModel: ApplicationStatusModel) => {
            setApplicationStatuses(statusModel.applicationStatus);
            setTotalApplications(statusModel.totalApplications);
            setIsCvLess(statusModel.isPreApplicationFormEnabled);
            setUnreadCount(statusModel.unreadApplicationsCount);
            const totalPageCount = statusModel.totalApplications > 0
                ? Math.ceil(statusModel.totalApplications / pageSize)
                : 1;
            setTotalPages(totalPageCount);
            setStatusLoading(false);
            })
        .catch((error) => {
            toErrorPage(error, UserType.Recruiter);
        });
    }

    async function getApplicantList(shouldScroll: boolean = false) {
        setApplicantsLoading(true);
        let searchCriteria: ApplicantsSearchCriteria = {
            jobId: jobId,
            statusId: statusId,
            page: page,
            pageSize: pageSize,
            sortOrder: sortOrder,
            recruiterId: recruiter!.recruiterId,
            isAppRead: appReadStatus,
            keyWord: keyword
        }
        await getApplicants(searchCriteria, recruiter!.token)
        .then((applicantList: JobApplication[]) => {
            setApplicants(applicantList);
            setApplicantsLoading(false);
            let totalPageCount = Math.ceil((applicantList[0]?.totalRecordsCount ?? 0) / pageSize); 
            setTotalPages(totalPageCount);
            if (shouldScroll && isMobile && candidateSortRef.current) {
                candidateSortRef.current.scrollIntoView();
            }    
        })
        .catch((error) => {
            toErrorPage(error, UserType.Recruiter);
        });
    }

    async function updateStatus () {
        const targetStatus = applicationStatuses?.find(
            (status) => status.status === actionStatus
        );
        const applicationStatusId = targetStatus?.applicationStatusId;
        const selectedApplicantIds = Object.keys(singleSelect).filter(id => singleSelect[Number(id)]).join(",");
        if (!selectedApplicantIds) {
            setOpenMessageDialog(false);
            return;
        }
        await updateApplicationStatuses(jobId, applicationStatusId!, selectedApplicantIds, recruiter!.recruiterId, recruiter!.token)
        .then((response) => {
            setApplicationStatuses(response.applicationStatus);
            setUnreadCount(response.unreadApplicationsCount);
            setOpenMessageDialog(false);
            setSingleSelect({});
            setAllSelect(false);
            setDisplayTabs(false);
            getApplicantList();
        })
        .catch((error) => {            
            toErrorPage(error, UserType.Recruiter);
        });
    }

    async function downloadApplicantCV() {
        let downloadAll = !hasSelection;
        const applicantsToDownload = downloadAll
            ? filteredApplicants.filter(applicant => !applicant.deleted && applicant.hasSentToRecruiter)
            : applicants.filter(applicant => applicant.hasSentToRecruiter &&
                singleSelect[applicant.preApplicationId ? applicant.preApplicationId : applicant.jobApplicationId
            ])
        const selectedApplicantIdList = applicantsToDownload
            .map(applicant => isCvLess ? applicant.preApplicationId : applicant.jobApplicationId);
        const selectedApplicantIdString = selectedApplicantIdList.join(",");
        await downloadApplicantResumes(selectedApplicantIdString, jobTitle!, jobId, recruiter!.recruiterId, downloadAll, recruiter!.token)
        .then((response: any) => {
            setApplicants(prev => prev.map(x => {
                const id = isCvLess ? x.preApplicationId! : x.jobApplicationId!;
                return selectedApplicantIdList.includes(id) ? { ...x, hasReadByRecruiter: true } : x;
            }));
            downloadAll
                ? setUnreadCount(0)
                : setUnreadCount(prev => Math.max(0, prev - applicantsToDownload.filter(a => !a.hasReadByRecruiter).length));
        })
        .catch((error) => {
            toErrorPage(error, UserType.Recruiter);            
        });
    }

    async function updateReadStatus (applicant: JobApplication) {
        await updateApplicationReadStatus(recruiter!.recruiterId, recruiter!.token, applicant.preApplicationId, applicant.jobApplicationId)
        .then((response) => {
            if (response.status) {
                setApplicants(prev => 
                    prev.map(x => {
                        if (x.jobApplicationId === applicant.jobApplicationId && x.preApplicationId === applicant.preApplicationId) {
                            return { ...x, hasReadByRecruiter: true };
                        }
                        return x;
                    })
                );
                if (!applicant.hasReadByRecruiter) {
                    setUnreadCount(prev => Math.max(0, prev - 1));
                }
            }
        })
        .catch((error) => {            
            toErrorPage(error, UserType.Recruiter);
        });
    }

    async function handleViewFullScreenCV(applicant: JobApplication) {
        try {
            if (!applicant?.hasReadByRecruiter) {
                updateReadStatus(applicant);
            }
            await getCandidateCvDetails(recruiter!.recruiterId, applicant.candidate!.candidateId, recruiter!.token)
                .then(async (response: any) => {
                    const openInBrowser = true;
                    const url = getCandidateCVUrlByRecruiter(recruiter!.recruiterId, response.cvTranslationText, recruiter!.token, openInBrowser);
                    window.open(url, 'CV', '_blank');
                });
        } catch (error) {
            toErrorPage(error, UserType.Recruiter);
        }
    }

    return (
        <>
            <Helmet><title>Vacancy Manager - XpressJobs</title></Helmet>
            <div style={styles.mainWrapper}>
                <div style={styles.leftWrapper}>
                    {(isMobile || (!isMobile && openSideMenu)) && <RecruiterMenu>
                        {(isMobile && showEmailComponent) ? (
                            <p style={styles.emailContainer.title}>{"Send a new email"}</p>
                        ) : (
                            <form style={styles.search} onSubmit={handleSearch}>
                                <input type="text" style={{...styles.search, ...styles.search.input}} placeholder={"Search"} value={keyword} onChange={(e) => setKeyword(e.target.value)}/>
                                <Button type="submit" style={styles.search.iconWrapper}>
                                    <SearchIcon style={styles.search.icon}/>
                                </Button>  
                            </form>
                        )}
                    </RecruiterMenu>}
                    {!isMobile && (
                        <Button style={openSideMenu ? styles.closeButton : styles.expand} onClick={handleSideMenuToggle}>
                            <Arrow style={openSideMenu ? styles.closeButtonIcon : styles.expand.icon} />
                        </Button>
                    )}
                </div>
                {(isMobile && showEmailComponent) ? (
                    <EmailToCandidate 
                        recipientEmails={recipientEmails} 
                        candidateIds={candidateIds}
                        jobId={jobId}
                        jobTitle={jobTitle!}
                        recruiterId={recruiter!.recruiterId}
                        token={recruiter!.token}
                        style={styles.emailContainer} 
                        onClose={closeEmailComponent} 
                        onParentAlert={openAlert}
                    />
                ) : (isMobile && displayTabs) ? (
                        profileLoading ? (
                            <CandidateTabPanelLoader />
                        ) : (
                            <ApplicationTabPanel
                                applicant={selectedApplicant!}
                                style={styles.popUpContainer}
                                tabItems={tabItems}
                                activeTab={activeTab}
                                applicationStatuses={applicationStatuses}
                                onStatusOptionClick={handlePanelStatusOptionClick}
                                onClose={closePopUp}
                            />
                        )
                    ) : (
                        <div style={styles.rightWrapper}>
                            <div style={styles.searchWrapper}>
                                <div style={styles.backButton.wrapper}>
                                    <div style={styles.backButton.container} onClick={navigateToHome}>
                                        <Button style={styles.backButton.button}><LeftArrow style={styles.backButton.icon} /></Button>
                                        <span style={styles.backButton.text}>{"Back"}</span>
                                    </div>
                                </div>
                                <div style={styles.profileWrapper}><ProfileEntrance recruiter={recruiter} /></div>
                            </div>
                            <div style={styles.titleWrapper}>
                                <div>
                                    <div style={{...styles.titleWrapper.title, ...(openSideMenu && styles.titleWrapper.title.expanded)}}>{jobTitle}</div>
                                </div>
                                {!isMobile && (
                                    <>
                                        <form style={styles.search} onSubmit={handleSearch}>
                                            <input type="text" className="search-input" style={{...styles.search, ...styles.search.input}} placeholder={"Search"} value={keyword} onChange={(e) => setKeyword(e.target.value)}/>
                                            <Button type="submit" style={styles.search.iconWrapper}>
                                                <SearchIcon style={styles.search.icon}/>
                                            </Button>  
                                        </form>
                                    </>
                                )}
                            </div>
                            <div style={styles.statusBoxes}>
                                {statusLoading ? <VacancyCountLoader/> :
                                <>
                                    {visibleStages.map((stage) => (
                                        <div 
                                            key={stage}
                                            style={{...styles.statusBoxes.box, ...(selectedStage === stage && styles.statusBoxes.selected)}} 
                                            onClick={() => handleStageChange(stage)}
                                        >
                                            <div style={styles.statusBoxes.box.count}>{getStageTotal(stage)}</div>
                                            <div style={styles.statusBoxes.box.label}>{stage}</div>
                                        </div>
                                    ))}
                                </>}
                            </div>
                            {(statusLoading || stageStatuses.length > 0) && (
                                <div style={{...styles.status, ...(openSideMenu && styles.status.showSideMenu)}}>
                                    {getStageStatuses(selectedStage).map((status) => (
                                        <div key={status.applicationStatusId} 
                                            style={{
                                                ...styles.status.tag, 
                                                ...(statusId === status.applicationStatusId && styles.status.selected)
                                            }} 
                                            onClick={() => handleStatusChange(status.applicationStatusId)}
                                        >
                                            <div style={{...styles.status.statusDot, backgroundColor: status.colorCode}}></div>
                                            {status.status} {status.applicationsCount> 0 && (" (" + status.applicationsCount + ")")}
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div style={styles.content}>
                                <div style={{ width: '100%' }}>
                                    {isMobile && (
                                        <>
                                            {filteredApplicants.length > 0 && (
                                                <div style={styles.downloadAllCv} onClick={downloadApplicantCV}>
                                                    {!hasSelection ? "Download All CVs" : "Download Selected"}
                                                </div>
                                            )}
                                            {unreadCount > 0 && (
                                                <Button style={styles.unreadCount} onClick={handleUnreadClick}>
                                                    {unreadCount + " Unread Applications"}
                                                </Button>
                                            )}
                                        </>
                                    )}
                                    <div id="candidateSort" ref={candidateSortRef} style={styles.selectWrapper}>  
                                        <div style={styles.selectRow}>
                                            <SingleSelect key={`sort-${appReadStatus}-${nameSortAsc}`} styles={styles.sortSelect} selectInputs={sortBySelectInputs} getSelected={handleSortBySelect} />
                                            {isMobile && filteredApplicants.length > 0 && currentViewTotal > 15 && 
                                                <SingleSelect styles={styles.perPageSelect} label={"Posts Per Page"} selectInputs={perPageSelectInputs} getSelected={setPageSize} />
                                            }
                                            {!isMobile && <div style={{...styles.actionWrapper.fadeContainer, ...(hasSelection && styles.actionWrapper.fadeContainerVisible)}}>
                                                {hasSelection && (
                                                    <div style={styles.actions} ref={actionsSelectRef}>
                                                        <label style={styles.actions.label}>{"Actions"}</label>
                                                        <div
                                                            style={styles.actions.button}
                                                            onClick={() => setOpenActionsSelectDropdown(prev => !prev)}
                                                        >
                                                            <div style={{...styles.actions.buttonWrapper, ...(openActionsSelectDropdown && styles.actions.buttonWrapper.hover)}}>
                                                                <div style={styles.actions.buttonText}>{"Move to"}</div>
                                                                <Arrow style={{...styles.actions.buttonIcon, ...(openActionsSelectDropdown && styles.actions.buttonIcon.hover)}}/>
                                                            </div>
                                                            {openActionsSelectDropdown && (
                                                                <div className={"scrollArea"} style={styles.actions.hover} onClick={(e) => e.stopPropagation()}>
                                                                    {groupedStatusOptions.map((group, groupIndex) => (
                                                                        <div
                                                                            key={group.stage}
                                                                            style={{
                                                                                ...styles.actions.group,
                                                                                ...(groupIndex === groupedStatusOptions.length - 1 && styles.actions.group.lastItem)
                                                                            }}
                                                                        >
                                                                            <div style={styles.actions.groupHeader}>
                                                                                {group.stage.toUpperCase()}
                                                                            </div>
                                                                            {group.items.map((status) => {
                                                                                const optionKey = status.applicationStatusId;
                                                                                return (
                                                                                    <div
                                                                                        key={status.applicationStatusId}
                                                                                        style={{
                                                                                            ...styles.actions.optionWrapper,
                                                                                            ...(hoveredStatusOption === optionKey && styles.actions.optionWrapper.hovered)
                                                                                        }}
                                                                                        onClick={() => handleActionStatusSelect(status)}
                                                                                        onMouseEnter={() => setHoveredStatusOption(optionKey)}
                                                                                        onMouseLeave={() => setHoveredStatusOption(null)}
                                                                                    >
                                                                                        <span style={{...styles.actions.optionDot, backgroundColor: status.colorCode}}/>
                                                                                        <div style={styles.actions.optionWrapper.status}>{status.status}</div>
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>}
                                        </div>
                                        {!isMobile && (
                                            <>
                                                {hasSelection && (<div style={styles.topActionMenu.buttonWrapper}>
                                                    <Button style={{...styles.topActionMenu.buttons, ...(styles.topActionMenu.email)}} onClick={() => openEmailComponent()}>
                                                        {"Email"}
                                                    </Button>
                                                </div>)}
                                                {filteredApplicants.length > 0 && (
                                                    <div style={styles.downloadAllCv} onClick={downloadApplicantCV}>
                                                        {!hasSelection ? "Download All CVs" : "Download Selected"}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div> 
                                    {(isMobile && filteredApplicants.length > 0) && <div style={styles.actionWrapper}>  
                                        <CheckboxInput styles={styles.actionWrapper.checkBoxRow} name="SelectAll" value={allSelect} handleChange={handleAllSelectChange}>
                                            <span style={styles.actionLabel}>{"All"}</span>
                                        </CheckboxInput>
                                        <div style={styles.actionWrapper.selectActions}>
                                            <div style={{...styles.actionWrapper.fadeContainer, ...(hasSelection && styles.actionWrapper.fadeContainerVisible)}}>
                                                {hasSelection && (
                                                    <div style={styles.actions} ref={actionsSelectRef}>
                                                        <label style={styles.actions.label}>{"Actions"}</label>
                                                        <div
                                                            style={styles.actions.button}
                                                            onClick={() => setOpenActionsSelectDropdown(prev => !prev)}
                                                        >
                                                            <div style={{...styles.actions.buttonWrapper, ...(openActionsSelectDropdown && styles.actions.buttonWrapper.hover)}}>
                                                                <div style={styles.actions.buttonText}>{"Move to"}</div>
                                                                <Arrow style={{...styles.actions.buttonIcon, ...(openActionsSelectDropdown && styles.actions.buttonIcon.hover)}}/>
                                                            </div>
                                                            {openActionsSelectDropdown && (
                                                                <div style={styles.actions.hover} onClick={(e) => e.stopPropagation()}>
                                                                    {groupedStatusOptions.map((group, groupIndex) => (
                                                                        <div
                                                                            key={group.stage}
                                                                            style={{
                                                                                ...styles.actions.group,
                                                                                ...(groupIndex === groupedStatusOptions.length - 1 && styles.actions.group.lastItem)
                                                                            }}
                                                                        >
                                                                            <div style={styles.actions.groupHeader}>
                                                                                {group.stage.toUpperCase()}
                                                                            </div>
                                                                            {group.items.map((status) => (
                                                                                <div
                                                                                    key={status.applicationStatusId}
                                                                                    style={styles.actions.optionWrapper}
                                                                                    onClick={() => handleActionStatusSelect(status)}
                                                                                >
                                                                                    <span style={{...styles.actions.optionDot, backgroundColor: status.colorCode}}/>
                                                                                    <div style={styles.actions.optionWrapper.status}>{status.status}</div>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>}
                                    {isMobile && hasSelection && (<div style={styles.topActionMenu.buttonWrapper}>
                                        <Button style={{...styles.topActionMenu.buttons, ...(styles.topActionMenu.email)}} onClick={() => openEmailComponent()}>
                                            {"Email"}
                                        </Button>
                                    </div>)}
                                    {(!isMobile && filteredApplicants.length > 0) && <div style={styles.selectAllWrapper}>  
                                        <CheckboxInput styles={styles.selectAllWrapper.checkBoxRow} name="SelectAll" value={allSelect} handleChange={handleAllSelectChange}>
                                            <div style={styles.selectAllWrapper.selectAllLabel}>{"Select all"}</div>
                                        </CheckboxInput>
                                        {unreadCount > 0 && (
                                            <Button style={styles.unreadCount} onClick={handleUnreadClick}>
                                                {unreadCount + " Unread Applications"}
                                            </Button>
                                        )}
                                    </div>}  
                                    <div style={{...styles.candidateList, ...(!applicantsLoading && filteredApplicants.length === 0 && styles.candidateList.noCandidates)}}>                            
                                        {applicantsLoading ? <VacancyListLoader/> :
                                        <>  
                                            {filteredApplicants.length > 0 ? <>   
                                                {filteredApplicants.map((applicant, index) => {
                                                    const id = (applicant.hasSentToRecruiter && applicant.preApplicationId) ? applicant.preApplicationId : applicant.jobApplicationId;
                                                    return (
                                                        <div 
                                                            key={id}
                                                            style={{
                                                                ...styles.candidateCard,                                                                
                                                                ...(applicant.hasReadByRecruiter && styles.candidateCard.readApplicant),
                                                                ...(applicant.deleted && styles.candidateCard.deleted),
                                                                ...(hoveredIndex === index && styles.candidateCard.hovered),
                                                                ...(index === 0 && styles.candidateCard.firstItem),
                                                                ...(index === filteredApplicants.length - 1 && styles.candidateCard.lastItem)
                                                            }}
                                                            onMouseEnter={() => setHoveredIndex(index)}
                                                            onMouseLeave={() => setHoveredIndex(-1)}
                                                            onClick={(e) => handleApplicantClick(e, applicant)}
                                                        >
                                                            <div onClick={(e) => e.stopPropagation()}>
                                                                <CheckboxInput
                                                                    styles={styles.candidateCard.checkBox}
                                                                    value={singleSelect[id] || false}
                                                                    handleChange={() => handleSingleSelectChange(id)}
                                                                    disabled={applicant.deleted || !applicant.hasSentToRecruiter}
                                                                />
                                                            </div>
                                                            <div style={styles.candidateCard.userProfile}>
                                                                {applicant.hasSentToRecruiter ? (
                                                                    <LazyProfileImage
                                                                        candidateId={applicant.candidate!.candidateId}                                                                        
                                                                        style={styles.candidateCard.userAvatar}
                                                                    />
                                                                ) : (
                                                                    <DefaultUser style={styles.candidateCard.userAvatar}/>
                                                                )}
                                                                <div style={{...styles.candidateCard.details, ...(openSideMenu && styles.candidateCard.details.expanded)}}> 
                                                                    <div 
                                                                        style={{
                                                                            ...styles.candidateCard.nameWrapper,
                                                                            ...(openSideMenu && styles.candidateCard.nameWrapper.expanded),
                                                                            ...(!openSideMenu && styles.candidateCard.nameWrapper.listView)
                                                                        }}
                                                                    >
                                                                        <div 
                                                                            style={{
                                                                                ...styles.candidateCard.name,
                                                                                ...(openSideMenu ? styles.candidateCard.name.listViewAndExpanded : styles.candidateCard.name.listView)
                                                                            }}
                                                                        >   
                                                                            {applicant.candidate!.forename} {applicant.candidate!.surname}
                                                                        </div>
                                                                        {applicant.candidate!.isPremium && 
                                                                        <div style={styles.candidateCard.crownWrapper} 
                                                                            onMouseEnter={() => setHoveredPremiumApplicant(index)} 
                                                                            onMouseLeave={() => setHoveredPremiumApplicant(null)}
                                                                        >
                                                                            <Crown style={styles.candidateCard.crownIcon}/>
                                                                            {hoveredPremiumApplicant === index && <div style={styles.premiumMessage}>
                                                                                <div style={styles.premiumMessage.pointer}></div>
                                                                                    {"Premium Candidate"}
                                                                            </div>}
                                                                        </div>}
                                                                    </div> 
                                                                    <div style={styles.colomnWrapper}>
                                                                        {applicant.candidate!.homeTown && applicant.hasSentToRecruiter &&
                                                                            <div 
                                                                                style={{
                                                                                    ...styles.candidateCard.address,
                                                                                    ...styles.candidateCard.address.expanded,
                                                                                    ...((!openSideMenu) && styles.candidateCard.address.listView)
                                                                                }}
                                                                            >
                                                                                {applicant.candidate!.homeTown}
                                                                            </div>
                                                                        }
                                                                        {(applicant.preApplicationId !=0 && applicant.jobApplicationId == 0) && <CVLess style={styles.candidateCard.cvLessIcon}/>}
                                                                    </div>
                                                                    {isMobile && <div style={styles.candidateCard.statusBadgeWrapper}>
                                                                        <div style={styles.candidateCard.dateWrapper}>
                                                                            <div><Calendar style={styles.candidateCard.calendarIcon} /></div>
                                                                            <div style={styles.candidateCard.applied}>{"Applied "}</div>
                                                                            <div style={styles.candidateCard.date}>{formatDateWithSuperscript(applicant.createdDate)}</div>
                                                                        </div>
                                                                        <div
                                                                            style={styles.candidateCard.statusBadgeContainer}
                                                                            ref={openStatusDropdownId === id ? statusDropdownRef : null}
                                                                        >
                                                                            <div
                                                                                style={{
                                                                                    ...styles.candidateCard.statusBadge, color: applicant.applicationStatus!.colorCode, backgroundColor: lightenColor(applicant.applicationStatus!.colorCode, 0.15),
                                                                                    ...(applicant.applicationStatus!.status == "New Applicant" && {...styles.candidateCard.statusBadge.newApplicant}),
                                                                                    ...(applicant.applicationStatus!.status == "Fake Answered" && {...styles.candidateCard.statusBadge.fakeAnswered}),
                                                                                    ...(applicant.applicationStatus!.status == "Quiz Incomplete" && {...styles.candidateCard.statusBadge.quizIncomplete}),
                                                                                    ...(applicant.applicationStatus!.status == "Screening Failed" && {...styles.candidateCard.statusBadge.screeningFailed}),
                                                                                    ...(applicant.applicationStatus!.status == "Screening Passed" && {...styles.candidateCard.statusBadge.screeningPassed}),
                                                                                    ...(applicant.applicationStatus!.status == "Screening Incomplete" && {...styles.candidateCard.statusBadge.screeningIncomplete}),
                                                                                    ...(applicant.applicationStatus!.status == "Interview No Show" && {...styles.candidateCard.statusBadge.noShow}),
                                                                                    ...(applicant.applicationStatus!.status == "Booked for Interview 1" && {...styles.candidateCard.statusBadge.interviewOne}),
                                                                                    ...(applicant.applicationStatus!.status == "Booked for Interview 2" && {...styles.candidateCard.statusBadge.interviewTwo})
                                                                                }}
                                                                                onClick={(e) => applicant.hasSentToRecruiter && !applicant.deleted && toggleStatusDropdown(e, id)}
                                                                            >
                                                                                {applicant.applicationStatus!.status}
                                                                                {applicant.hasSentToRecruiter && <Arrow style={{...styles.candidateCard.statusBadge.chevron, ...(openStatusDropdownId === id && styles.candidateCard.statusBadge.chevron.open)}}/>}
                                                                            </div>
                                                                            {openStatusDropdownId === id && (
                                                                                <div style={styles.candidateCard.statusMenu} onClick={(e) => e.stopPropagation()}>
                                                                                    {groupedStatusOptions.map((group, groupIndex) => (
                                                                                        <div 
                                                                                            key={group.stage} 
                                                                                            style={{
                                                                                                ...styles.candidateCard.statusMenu.group,
                                                                                                ...(groupIndex === groupedStatusOptions.length - 1 && styles.candidateCard.statusMenu.group.lastItem)
                                                                                            }}
                                                                                        >
                                                                                            <div style={styles.candidateCard.statusMenu.groupHeader}>
                                                                                                {group.stage.toUpperCase()}
                                                                                            </div>
                                                                                            {group.items.map((status) => (
                                                                                                <div
                                                                                                    key={status.applicationStatusId}
                                                                                                    style={{
                                                                                                        ...styles.candidateCard.statusMenu.option, 
                                                                                                        ...(status.status === applicant.applicationStatus?.status && styles.candidateCard.statusMenu.option.current)
                                                                                                    }}
                                                                                                    onClick={(e) => handleStatusOptionClick(e, applicant, status, id)}
                                                                                                >
                                                                                                    <span style={{...styles.candidateCard.statusMenu.dot, backgroundColor: status.colorCode}}/>
                                                                                                    <span style={styles.candidateCard.statusMenu.label}>{status.status}</span>
                                                                                                    {status.status === applicant.applicationStatus?.status && <TickMark style={styles.candidateCard.statusMenu.tick}/>}
                                                                                                </div>
                                                                                            ))}
                                                                                        </div>
                                                                                    ))}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>}
                                                                </div>
                                                                {!isMobile && (
                                                                <> 
                                                                    <div style={styles.candidateCard.statusWrapper}>
                                                                        <div
                                                                            style={styles.candidateCard.statusBadgeContainer}
                                                                            ref={openStatusDropdownId === id ? statusDropdownRef : null}
                                                                        >
                                                                            <div
                                                                                style={{
                                                                                    ...styles.candidateCard.statusBadge, color: applicant.applicationStatus!.colorCode, backgroundColor: lightenColor(applicant.applicationStatus!.colorCode, 0.15),
                                                                                    ...(applicant.applicationStatus!.status == "Screening Incomplete" && {...styles.candidateCard.statusBadge.screeningIncomplete}),
                                                                                    ...(applicant.applicationStatus!.status == "Booked for Interview 1" && {...styles.candidateCard.statusBadge.interviewOne}),
                                                                                    ...(applicant.applicationStatus!.status == "Booked for Interview 2" && {...styles.candidateCard.statusBadge.interviewTwo}),
                                                                                    ...(applicant.applicationStatus!.status == "Interview No Show" && {...styles.candidateCard.statusBadge.noShow})
                                                                                }}
                                                                                onClick={(e) => applicant.hasSentToRecruiter && !applicant.deleted && toggleStatusDropdown(e, id)}
                                                                            >
                                                                                {applicant.applicationStatus!.status}
                                                                                {applicant.hasSentToRecruiter && <Arrow style={{...styles.candidateCard.statusBadge.chevron, ...(openStatusDropdownId === id && styles.candidateCard.statusBadge.chevron.open)}}/>}
                                                                            </div>
                                                                            {openStatusDropdownId === id && (
                                                                                <div style={styles.candidateCard.statusMenu} onClick={(e) => e.stopPropagation()}>
                                                                                    {groupedStatusOptions.map((group, groupIndex) => (
                                                                                        <div 
                                                                                            key={group.stage} 
                                                                                            style={{
                                                                                                ...styles.candidateCard.statusMenu.group,
                                                                                                ...(groupIndex === groupedStatusOptions.length - 1 && styles.candidateCard.statusMenu.group.lastItem)
                                                                                            }}
                                                                                        >
                                                                                            <div style={{...styles.candidateCard.statusMenu.groupHeader}}>
                                                                                                {group.stage.toUpperCase()}
                                                                                            </div>
                                                                                            {group.items.map((status) => {
                                                                                                const optionKey = status.applicationStatusId;
                                                                                                return (
                                                                                                    <div
                                                                                                        key={status.applicationStatusId}
                                                                                                        style={{
                                                                                                            ...styles.candidateCard.statusMenu.option,
                                                                                                            ...(status.status === applicant.applicationStatus?.status && styles.candidateCard.statusMenu.option.current),
                                                                                                            ...(hoveredStatusOption === optionKey && styles.candidateCard.statusMenu.option.hovered)
                                                                                                        }}
                                                                                                        onClick={(e) => handleStatusOptionClick(e, applicant, status, id)}
                                                                                                        onMouseEnter={() => setHoveredStatusOption(optionKey)}
                                                                                                        onMouseLeave={() => setHoveredStatusOption(null)}
                                                                                                    >
                                                                                                        <span style={{...styles.candidateCard.statusMenu.dot, backgroundColor: status.colorCode}}/>
                                                                                                        <span style={styles.candidateCard.statusMenu.label}>{status.status}</span>
                                                                                                        {status.status === applicant.applicationStatus?.status && <TickMark style={styles.candidateCard.statusMenu.tick}/>}
                                                                                                    </div>
                                                                                                )
                                                                                            })}
                                                                                        </div>
                                                                                    ))}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                    {applicant.jobApplicationId > 0 && applicant.hasSentToRecruiter && !applicant.deleted &&(
                                                                        <Button
                                                                            style={styles.candidateCard.viewCvIcon}
                                                                            onClick={(e) => { e.stopPropagation(); handleViewFullScreenCV(applicant); }}
                                                                        >
                                                                            <Document style={styles.candidateCard.viewCvIcon.icon}/>
                                                                        </Button>
                                                                    )}
                                                                    <div 
                                                                        style={{
                                                                            ...styles.candidateCard.dateWrapper,  
                                                                            ...((hoveredIndex === index) && styles.candidateCard.listView.hovered),
                                                                            ...((openSideMenu) && styles.candidateCard.listView.expanded),
                                                                            ...((openSideMenu && hoveredIndex === index) && styles.candidateCard.listView.expandedAndHovered)
                                                                        }}
                                                                    >
                                                                        <div style={styles.candidateCard.appliedDate}>
                                                                            <Calendar style={styles.candidateCard.calendarIcon} />
                                                                            <div style={styles.candidateCard.dateContainer}>
                                                                                <div style={styles.candidateCard.applied}>{"Applied "}</div>
                                                                                <div style={styles.candidateCard.date}>
                                                                                    {formatDateWithSuperscript(applicant.createdDate)}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div> 
                                                                    <div
                                                                        style={styles.candidateCard.actionsMenuContainer}
                                                                        ref={openActionsMenuId === id ? actionsMenuRef : null}
                                                                    >
                                                                        <Button
                                                                            style={styles.candidateCard.actionsMenuButton}
                                                                            onClick={(e) => toggleActionsMenu(e, id)}
                                                                            disabled={applicant.deleted}
                                                                        >
                                                                            <ThreeDots style={styles.candidateCard.actionsMenuButton.icon}/>
                                                                        </Button>
                                                                        {openActionsMenuId === id && (() => {
                                                                            const isCvLessApplicant = applicant.preApplicationId !== 0 && applicant.jobApplicationId === 0;
                                                                            const viewCvDisabled = !applicant.hasSentToRecruiter;
                                                                            const answersDisabled = applicant.applicationStatus?.status === "Screening Incomplete" || (!applicant.hasAnswered && applicant.preApplicationId === 0) || !applicant.hasSentToRecruiter;
                                                                            const moveDisabled = !applicant.hasSentToRecruiter;
                                                                            const notesDisabled = !applicant.hasSentToRecruiter;
                                                                            return (
                                                                                <div style={styles.candidateCard.actionsMenu} onClick={(e) => e.stopPropagation()}>
                                                                                    <Button
                                                                                        style={{...styles.candidateCard.actionsMenuItem, ...(viewCvDisabled && styles.candidateCard.actionsMenuItem.disabled)}}
                                                                                        onClick={(e) => handleActionMenuItemClick(e, applicant, isCvLessApplicant ? "Answers" : "CV")}
                                                                                        disabled={viewCvDisabled}
                                                                                    >
                                                                                        <Document style={styles.candidateCard.actionsMenuItem.icon}/>
                                                                                        {"View CV"}
                                                                                    </Button>
                                                                                    <Button
                                                                                        style={{...styles.candidateCard.actionsMenuItem, ...(answersDisabled && styles.candidateCard.actionsMenuItem.disabled)}}
                                                                                        onClick={(e) => handleActionMenuItemClick(e, applicant, "Answers")}
                                                                                        disabled={answersDisabled}
                                                                                    >
                                                                                        <Answers style={styles.candidateCard.actionsMenuItem.icon}/>
                                                                                        {"Answers"}
                                                                                    </Button>
                                                                                    <Button
                                                                                        style={{...styles.candidateCard.actionsMenuItem, ...(moveDisabled && styles.candidateCard.actionsMenuItem.disabled)}}
                                                                                        onClick={(e) => handleActionMenuItemClick(e, applicant, "Status")}
                                                                                        disabled={moveDisabled}
                                                                                    >
                                                                                        <Move style={styles.candidateCard.actionsMenuItem.icon}/>
                                                                                        {"Move"}
                                                                                    </Button>
                                                                                    <Button
                                                                                        style={{...styles.candidateCard.actionsMenuItem, ...(notesDisabled && styles.candidateCard.actionsMenuItem.disabled)}}
                                                                                        onClick={(e) => handleActionMenuItemClick(e, applicant, "Notes")}
                                                                                        disabled={notesDisabled}
                                                                                    >
                                                                                        <Notes style={styles.candidateCard.actionsMenuItem.icon}/>
                                                                                        {"Notes"}
                                                                                    </Button>
                                                                                </div>
                                                                            );
                                                                        })()}
                                                                    </div>
                                                                </>)}
                                                            </div>
                                                        </div>
                                                    )}
                                                )}
                                            </> : <FeedbackCard
                                                    styles={styles.noApplicants}
                                                    message={`No applicants were found.`}
                                                />
                                            }
                                        </>}
                                    </div>
                                    {!applicantsLoading && filteredApplicants.length > 0 && currentViewTotal > 15 && (
                                        <div style={{
                                                ...styles.paginationWrapper,
                                                ...(!isMobile && filteredApplicants.length <= 2 && styles.paginationWrapper.singleApplicant)
                                            }}
                                        >
                                            {!isMobile && <SingleSelect styles={styles.perPageSelect} label={"Posts Per Page"} selectInputs={perPageSelectInputs} getSelected={setPageSize} />}
                                            {(totalPages! > 1) && <Pagination page={page} totalPages={totalPages!} handlePagination={handlePagination} extendedStyles={styles.pagination} />}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )
                }
            </div>
            <Alert />
            <Footer/>
            <PopUpWindow styles={styles.confirmationDialog} open={openMessageDialog} toggle={closeMessageDialog}>
                <p style={styles.confirmationDialog.title}>{"Please Confirm"}</p>
                <div style={styles.confirmationDialog.message}>{"This will move the selected item. Do you want to continue?"}</div>
                <div style={styles.confirmationDialog.buttons}>
                    <Button style={styles.confirmationDialog.move} type="button" onClick={updateStatus}>{"Move"}</Button>
                    <Button style={styles.confirmationDialog.cancel} type="button" onClick={closeMessageDialog}>{"Cancel"}</Button>
                </div> 
            </PopUpWindow>
            <PopUpWindow styles={styles.quotaExceededDialog} open={openQuotaDialog} toggle={closeQuotaDialog}>
                <p style={styles.quotaExceededDialog.title}>{"Quota Exceeded!"}</p>
                <Button style={styles.quotaExceededDialog.confirm} type="button" onClick={closeQuotaDialog}>{"OK"}</Button>
            </PopUpWindow>
            <PopUpWindow 
                styles={{...styles.popUpTabs, container: {...styles.popUpTabs.container, ...(popupVisible && styles.popUpTabs.containerOpen)}}}
                open={popupMounted}
                toggle={closePopUp} 
                closeCircle={true}
            > 
                {profileLoading ? (
                    <CandidateTabPanelLoader />
                ) : (
                    <ApplicationTabPanel
                        applicant={selectedApplicant!}
                        style={styles.popUpContainer}
                        tabItems={tabItems}
                        activeTab={activeTab}
                        applicationStatuses={applicationStatuses}
                        onStatusOptionClick={handlePanelStatusOptionClick}
                        onClose={closePopUp}
                    /> 
                )}
            </PopUpWindow>
            <PopUpWindow styles={styles.emailContainer} open={!isMobile && showEmailComponent} toggle={closeEmailComponent} closeCircle={!isMobile}>
                <EmailToCandidate 
                    recipientEmails={recipientEmails} 
                    candidateIds={candidateIds}
                    jobId={jobId}
                    jobTitle={jobTitle!} 
                    recruiterId={recruiter!.recruiterId}
                    token={recruiter!.token}
                    style={styles.emailContainer}
                    onClose={closeEmailComponent}
                    onParentAlert={openAlert} 
                />
            </PopUpWindow>
        </>
    )
}